import axios from "axios";
import { useMemo, useState } from "react";
import { Button, Nav } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import GenericCrudPanel from "../../components/common/crud/GenericCrudPanel";

const HR_BASE = `${process.env.REACT_APP_BACKEND_API_URL}/hr`;

const ATTENDANCE_STATUS_OPTIONS = [
  { value: "PRESENT", label: "Present" },
  { value: "ABSENT", label: "Absent" },
  { value: "HALF_DAY", label: "Half Day" },
  { value: "ON_LEAVE", label: "On Leave" },
  { value: "HOLIDAY", label: "Holiday" },
  { value: "WEEK_OFF", label: "Week Off" },
];

const MODULES = [
  {
    key: "attendance",
    title: "Attendance",
    endpoint: "/attendance/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "date", label: "Date", required: true, type: "date" },
      { name: "check_in_time", label: "Check In Time", type: "datetime-local" },
      { name: "check_out_time", label: "Check Out Time", type: "datetime-local" },
      { name: "status", label: "Status", required: true, type: "select", optionsKey: "attendanceStatusOptions" },
      { name: "remarks", label: "Remarks", type: "textarea" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Date", selector: (row) => row.date || "-", sortable: true },
      { name: "Check In", selector: (row) => row.check_in_time || "-", sortable: true },
      { name: "Check Out", selector: (row) => row.check_out_time || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
    ],
    initialForm: { employee: "", date: "", check_in_time: "", check_out_time: "", status: "PRESENT", remarks: "" },
  },
  {
    key: "leaverequest",
    title: "Leave Requests",
    endpoint: "/leaverequest/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "leave_type", label: "Leave Type", required: true, type: "select", optionsKey: "leaveTypeOptions" },
      { name: "start_date", label: "Start Date", required: true, type: "date" },
      { name: "end_date", label: "End Date", required: true, type: "date" },
      { name: "number_of_days", label: "Number of Days", required: true, type: "number" },
      { name: "reason", label: "Reason", type: "textarea" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Leave Type", selector: (row) => row.leave_type_data?.name || "-", sortable: true },
      { name: "From", selector: (row) => row.start_date || "-", sortable: true },
      { name: "To", selector: (row) => row.end_date || "-", sortable: true },
      { name: "Status", selector: (row) => row.status || "-", sortable: true },
    ],
    initialForm: { employee: "", leave_type: "", start_date: "", end_date: "", number_of_days: "", reason: "" },
  },
  {
    key: "leavebalance",
    title: "Leave Balances",
    endpoint: "/leavebalance/",
    fields: [
      { name: "employee", label: "Employee", required: true, type: "select", optionsKey: "employeeOptions" },
      { name: "leave_type", label: "Leave Type", required: true, type: "select", optionsKey: "leaveTypeOptions" },
      { name: "year", label: "Year", required: true, type: "number" },
      { name: "allocated_days", label: "Allocated Days", type: "number" },
      { name: "used_days", label: "Used Days", type: "number" },
      { name: "carried_forward_days", label: "Carried Forward Days", type: "number" },
    ],
    columns: [
      { name: "Employee", selector: (row) => row.employee_data?.name || row.employee_data?.employee_code || "-", sortable: true },
      { name: "Leave Type", selector: (row) => row.leave_type_data?.name || "-", sortable: true },
      { name: "Year", selector: (row) => row.year || "-", sortable: true },
      { name: "Allocated", selector: (row) => row.allocated_days ?? "-", sortable: true },
      { name: "Used", selector: (row) => row.used_days ?? "-", sortable: true },
    ],
    initialForm: { employee: "", leave_type: "", year: new Date().getFullYear(), allocated_days: "", used_days: "", carried_forward_days: "" },
  },
];

async function loadOptionsForModule(module) {
  const requests = {};
  if (["attendance", "leaverequest", "leavebalance"].includes(module.key)) {
    requests.employees = axios.get(`${HR_BASE}/employee/`);
  }
  if (["leaverequest", "leavebalance"].includes(module.key)) {
    requests.leaveTypes = axios.get(`${HR_BASE}/leavetype/`);
  }

  const keys = Object.keys(requests);
  const responses = await Promise.all(keys.map((key) => requests[key]));
  const resultByKey = {};
  keys.forEach((key, index) => {
    resultByKey[key] = responses[index].data || [];
  });

  return {
    employeeOptions: (resultByKey.employees || []).map((item) => ({ value: item.id, label: `${item.employee_code} - ${item.user_data?.name || ""}` })),
    leaveTypeOptions: (resultByKey.leaveTypes || []).map((item) => ({ value: item.id, label: item.name })),
    attendanceStatusOptions: ATTENDANCE_STATUS_OPTIONS,
  };
}

function AttendanceLeave() {
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [activeTab, setActiveTab] = useState("attendance");

  const handleDataFromTopNavBar = (permissiondata) => {
    setDataFromTopNavBar(permissiondata);
  };

  const activeModule = useMemo(() => MODULES.find((module) => module.key === activeTab) || MODULES[0], [activeTab]);

  const checkoutAttendance = async (row, reload) => {
    try {
      await axios.put(`${HR_BASE}/attendance/${row.id}/checkout/`);
      toast.success("Checked out successfully.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || "Error checking out.");
    }
  };

  const runLeaveAction = async (row, action, reload) => {
    try {
      await axios.put(`${HR_BASE}/leaverequest/${row.id}/${action}/`);
      toast.success(`Leave request ${action}d successfully.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.error || `Error performing ${action} on leave request.`);
    }
  };

  const renderExtraRowActions = (row, module, reload) => (
    <>
      {module.key === "attendance" && row.check_in_time && !row.check_out_time ? (
        <Button className="btn btn-sm btn-success border-0 mx-1" title="Check Out" onClick={() => checkoutAttendance(row, reload)}>
          <i className="fa fa-sign-out"></i>
        </Button>
      ) : null}
      {module.key === "leaverequest" && row.status === "PENDING" ? (
        <>
          <Button className="btn btn-sm btn-success border-0 mx-1" title="Approve" onClick={() => runLeaveAction(row, "approve", reload)}>
            <i className="fa fa-check"></i>
          </Button>
          <Button className="btn btn-sm btn-warning border-0 mx-1" title="Reject" onClick={() => runLeaveAction(row, "reject", reload)}>
            <i className="fa fa-times"></i>
          </Button>
        </>
      ) : null}
      {module.key === "leaverequest" && ["PENDING", "APPROVED"].includes(row.status) ? (
        <Button className="btn btn-sm btn-secondary border-0 mx-1" title="Cancel" onClick={() => runLeaveAction(row, "cancel", reload)}>
          <i className="fa fa-ban"></i>
        </Button>
      ) : null}
    </>
  );

  return (
    <div className="page-content-bg">
      <TopNavbar sidebarCollapsed={menuDrawerShow} topNavbarStatic={true} sendDataToParent={handleDataFromTopNavBar} />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="HR"
        activeSubMenu="AttendanceLeave"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div className={menuDrawerShow ? "row main-content-collapsed text-left py-0" : "row main-content text-left py-0"}>
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-sm-6 py-1">
              <Button className="btn btn-sm btn-inox-green border-0 mx-2" onClick={() => setMenuDrawerShow(!menuDrawerShow)}>
                {menuDrawerShow ? <span><i className="fa fa-chevron-left mx-1"></i></span> : <span><i className="fa fa-chevron-right mx-1"></i></span>}
              </Button>
              <span className="page-header-text">Attendance &amp; Leave</span>
            </div>
          </div>
        </div>

        <div className="col-12 text-left" style={{ marginTop: -5, paddingLeft: 20, paddingRight: 30 }}>
          <Nav variant="tabs" className="mb-3">
            {MODULES.map((module) => (
              <Nav.Item key={module.key}>
                <Nav.Link className="text-inox-green" active={activeTab === module.key} onClick={() => setActiveTab(module.key)}>
                  {module.title}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <GenericCrudPanel module={activeModule} baseUrl={HR_BASE} loadOptions={loadOptionsForModule} extraRowActions={renderExtraRowActions} />
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}

export default AttendanceLeave;
